/**
 * The Internal Audits Graph I/O (13-32), on 13-23's pattern (`recordService.ts`) and through the same Writer seam
 * (`commitAdapter.ts`): panels and pages call these through the Writer, never directly.
 *
 *   - **Read the list** (`listAudits`): ONE paged read of `Internal Audits`, every §2.6 column selected inside
 *     `$expand=fields($select=…)` (a `$select=fields/X` read 400s, and a swallowed 400 reads as empty). An error is
 *     thrown, never swallowed: an empty answer must mean "no audits".
 *   - **Read one** (`getAudit`): the item, then its `Internal Audits Audit Log` rows (`ParentRef eq 'AUD-<id>'`, the
 *     non-indexed Prefer header, paged), oldest first.
 *   - **Schedule** (`createAudit`): the gate again, the patch checked against the schedule columns, POST (retried on
 *     429 only), then each sibling draft under `ParentRef` = `AUD-<new id>` (composed from the returned id).
 *   - **Commit** (`commitAuditVerb`, D-13-L-4): the gate again (`canDoAuditVerb`), the patch checked against the
 *     verb's columns (never `CentroRef`), PATCH with `If-Match` and only those columns, then the drafts. A 412 re-reads
 *     the item: adopted only when every patch value is on the row and Graph names this actor as its last editor;
 *     otherwise `AuditConflictError` with the fresh row. A 404 is a conflict. Nothing is merged.
 *   - **Owed rows** (`postAuditRows`, D-13-L-5): one fresh sibling read, then only the drafts not already there.
 *
 * Retry policy: `graphClient.withRetry` only (D-13-07-1). No React.
 */

import {
  createItemFields,
  errorStatus,
  eTagOf,
  getItemEnvelope,
  getListId,
  listItemEnvelopes,
  updateItemFields,
} from './graphClient';
import type { GraphListItemEnvelope, RetryOptions } from './graphClient';
import { CommitError, patchAlreadyApplied } from './actionService';
import type { ActionIO } from './actionService';
import { alreadyPosted, emailOf, mapSiblingItem, postInOrder, prepareSibling, sortSiblings } from './recordService';
import type { CommitRecordResult, CreateRecordResult, RecordIO, SiblingRow } from './recordService';
import { INTERNAL_AUDITS, auditVerbColumns } from '../config/fieldset/internalAudits';
import { auditSelect, composeAuditRef, parseAuditRef } from '../lib/auditQuery';
import { canDoAuditVerb } from '../lib/auditVerbs';
import type { AuditAuthorityReason, AuditRow, AuditVerb } from '../lib/auditVerbs';
import { actionTag } from '../lib/outbox';
import { SIBLING_SELECT, siblingFilter } from '../lib/recordQuery';
import type { Actor } from '../types/actionRegister';
import type { SiblingDraft, VerbResult } from '../types/records';

/** The `useGraphClient` list-id keys, when a caller resolved them; otherwise resolved by title (cached, ISS-207). */
export const AUDIT_LIST_ID_KEYS = { parent: 'internalAudits', audit: 'internalAuditsAudit' } as const;

async function listIdOf(io: RecordIO, which: 'parent' | 'audit'): Promise<string> {
  const given = io.listIds?.[AUDIT_LIST_ID_KEYS[which]];
  if (given) return given;
  return getListId(io.client, io.siteId, which === 'parent' ? INTERNAL_AUDITS.listTitle : INTERNAL_AUDITS.auditListTitle);
}

function postRetry(io: ActionIO): RetryOptions {
  return { ...io.retry, retryOn: [429] };
}

// ---------------------------------------------------------------------------
// Mapping and reads
// ---------------------------------------------------------------------------

/** A Graph item → `AuditRow` (the composed `AUD-<id>`; the item eTag for `If-Match`). */
export function mapAuditItem(item: GraphListItemEnvelope): AuditRow {
  const fields: Record<string, unknown> = { ...(item.fields ?? {}) };
  const etagField = fields['@odata.etag'];
  delete fields['@odata.etag'];
  return {
    id: Number(item.id),
    ref: composeAuditRef(item.id),
    eTag: eTagOf(item) ?? (typeof etagField === 'string' ? etagField : ''),
    fields,
  };
}

/** Every audit, one paged list read (the `/audits` page). Throws on any failure. */
export async function listAudits(io: RecordIO): Promise<AuditRow[]> {
  const listId = await listIdOf(io, 'parent');
  const items = await listItemEnvelopes(io.client, io.siteId, listId, { select: auditSelect(), retry: io.retry });
  return items.map(mapAuditItem);
}

/** One audit's sibling rows, oldest first. */
export async function listAuditSiblings(io: RecordIO, ref: string): Promise<SiblingRow[]> {
  const { ref: canonical } = parseAuditRef(ref);
  const listId = await listIdOf(io, 'audit');
  const items = await listItemEnvelopes(io.client, io.siteId, listId, { select: SIBLING_SELECT, filter: siblingFilter(canonical), retry: io.retry });
  return sortSiblings(items.map(mapSiblingItem).filter((r) => r.parentRef === canonical));
}

export interface AuditRead {
  row: AuditRow;
  siblings: SiblingRow[];
  modifiedBy: string | null;
}

async function readAuditItem(io: RecordIO, id: number): Promise<GraphListItemEnvelope> {
  const listId = await listIdOf(io, 'parent');
  return getItemEnvelope(io.client, io.siteId, listId, id, auditSelect(), io.retry);
}

/** One audit by `AUD-<id>`, then its sibling rows. Either read failing throws (a 404 keeps its status). */
export async function getAudit(io: RecordIO, ref: string): Promise<AuditRead> {
  const { id, ref: canonical } = parseAuditRef(ref);
  const item = await readAuditItem(io, id);
  const row = mapAuditItem(item);
  const siblings = await listAuditSiblings(io, canonical);
  return { row, siblings, modifiedBy: emailOf(item.lastModifiedBy) };
}

// ---------------------------------------------------------------------------
// The gate (D-13-07-6) and the column check
// ---------------------------------------------------------------------------

/** The gate said no. Thrown before any Graph call. */
export class AuditRefusedError extends Error {
  readonly verb: AuditVerb;
  readonly reason: AuditAuthorityReason;

  constructor(verb: AuditVerb, reason: AuditAuthorityReason) {
    super(`${verb} refused: ${reason}`);
    this.name = 'AuditRefusedError';
    this.verb = verb;
    this.reason = reason;
  }
}

/** What the gate needs: the verb and the row the caller built it from (`null` to schedule). */
export interface AuditGate {
  verb: AuditVerb;
  row: AuditRow | null;
}

/** Throws `AuditRefusedError` unless `canDoAuditVerb` allows it now; the row must be the audit being written. */
export function checkAuditGate(gate: AuditGate, actor: Actor, ref?: string, now: Date = new Date()): void {
  if (!gate) throw new AuditRefusedError('schedule', 'no-row');
  if (ref !== undefined && gate.row?.ref !== ref) throw new Error(`internalAuditService: the gate's row ${gate.row?.ref ?? '(none)'} is not ${ref}`);
  const allowed = canDoAuditVerb(gate.verb, gate.row, actor, now);
  if (!allowed.ok) throw new AuditRefusedError(gate.verb, allowed.reason);
}

/** The patch holds only the verb's own columns, and never `CentroRef` (the carry-in's key). Throws otherwise. */
export function assertAuditPatch(verb: AuditVerb, patch: Record<string, unknown>): void {
  const allowed = new Set(auditVerbColumns(verb));
  for (const k of Object.keys(patch)) {
    if (k === 'CentroRef' || !allowed.has(k)) throw new Error(`internalAuditService: ${verb} may not write ${k}`);
  }
}

// ---------------------------------------------------------------------------
// Errors
// ---------------------------------------------------------------------------

/** The PATCH met a changed audit (412) or a deleted one (404, `current` null). Nothing was merged. */
export class AuditConflictError extends CommitError {
  readonly current: AuditRow | null;
  readonly columns: string[];

  constructor(cause: unknown, current: AuditRow | null, columns: string[]) {
    super('patch', cause, [], 412);
    this.name = 'AuditConflictError';
    this.current = current;
    this.columns = columns;
  }
}

// ---------------------------------------------------------------------------
// Schedule (create) and commit
// ---------------------------------------------------------------------------

/** A schedule draft's title gains the new ref: `schedule: DueOn` → `schedule AUD-12: DueOn`. */
export function prepareAuditSibling(draft: SiblingDraft, ref: string, actor: Pick<Actor, 'upn' | 'lookupId'>): SiblingDraft {
  const out = prepareSibling(draft, ref, actor);
  return /^schedule:/.test(out.Title) ? { ...out, Title: out.Title.replace(/^schedule:/, `schedule ${ref}:`) } : out;
}

/** Schedule an audit: the gate, the column check, the item, then its drafts under `AUD-<new id>`. */
export async function createAudit(io: RecordIO, result: VerbResult, actor: Actor): Promise<CreateRecordResult> {
  checkAuditGate({ verb: 'schedule', row: null }, actor);
  assertAuditPatch('schedule', result.patch);
  const parentList = await listIdOf(io, 'parent');
  let created: { id: string; eTag?: string };
  try {
    created = await createItemFields(io.client, io.siteId, parentList, result.patch, postRetry(io));
  } catch (err) {
    throw new CommitError('patch', err);
  }
  const id = Number(created.id);
  if (!Number.isSafeInteger(id) || id < 1) {
    throw new CommitError('patch', new Error('the create returned no item id: check the list before scheduling again'));
  }
  const ref = composeAuditRef(id);
  const drafts = result.siblings.map((d) => prepareAuditSibling(d, ref, actor));
  const auditList = await listIdOf(io, 'audit').catch(() => '');
  const auditPending = auditList ? await postInOrder(io, auditList, drafts) : drafts;
  return { ref, id, ...(created.eTag ? { eTag: created.eTag } : {}), auditPending };
}

function differingColumns(fields: Record<string, unknown>, patch: Record<string, unknown>): string[] {
  return Object.keys(patch).filter((k) => !patchAlreadyApplied({ id: '', fields }, { [k]: patch[k] }));
}

/** One audit verb save: the gate, then (if any) the PATCH with `If-Match`, then the drafts. */
export async function commitAuditVerb(
  io: RecordIO,
  ref: string,
  eTag: string,
  result: VerbResult,
  actor: Actor,
  gate: AuditGate,
): Promise<CommitRecordResult> {
  const { id, ref: canonical } = parseAuditRef(ref);
  checkAuditGate(gate, actor, canonical);
  assertAuditPatch(gate.verb, result.patch);
  const drafts = result.siblings.map((d) => prepareSibling(d, canonical, actor));
  const patch: Record<string, unknown> = { ...result.patch };
  let newETag: string | undefined;
  let adopted = false;
  let existing: SiblingRow[] | null = null;

  if (Object.keys(patch).length > 0) {
    if (!eTag) throw new CommitError('patch', new Error('an eTag is required (If-Match)'));
    const parentList = await listIdOf(io, 'parent');
    try {
      ({ eTag: newETag } = await updateItemFields(io.client, io.siteId, parentList, id, patch, eTag, io.retry));
    } catch (err) {
      const status = errorStatus(err);
      if (status === 404) throw new AuditConflictError(err, null, Object.keys(patch));
      if (status !== 412) throw new CommitError('patch', err);
      let item: GraphListItemEnvelope;
      try {
        item = await readAuditItem(io, id);
      } catch (readErr) {
        if (errorStatus(readErr) === 404) throw new AuditConflictError(err, null, Object.keys(patch));
        throw new CommitError('patch', readErr);
      }
      const current = mapAuditItem(item);
      const editor = emailOf(item.lastModifiedBy);
      if (!editor || editor !== actor.upn.trim().toLowerCase() || !patchAlreadyApplied(item, patch, actor.upn)) {
        throw new AuditConflictError(err, current, differingColumns(current.fields, patch));
      }
      adopted = true;
      newETag = current.eTag || undefined;
      try {
        existing = await listAuditSiblings(io, canonical);
      } catch {
        return { ...(newETag ? { eTag: newETag } : {}), auditPending: drafts, adopted };
      }
    }
  }

  const toPost = existing ? drafts.filter((d) => !alreadyPosted(existing as SiblingRow[], d)) : drafts;
  let auditPending: SiblingDraft[] = [];
  if (toPost.length > 0) {
    const auditList = await listIdOf(io, 'audit').catch(() => '');
    auditPending = auditList ? await postInOrder(io, auditList, toPost) : toPost;
  }
  return { ...(newETag ? { eTag: newETag } : {}), auditPending, adopted };
}

/**
 * Post drafts an audit write still owes, de-duped against ONE fresh sibling read. Returns what is still owed: every
 * draft when the read fails; an untagged draft always (never posted blind).
 */
export async function postAuditRows(io: RecordIO, ref: string, drafts: readonly SiblingDraft[]): Promise<SiblingDraft[]> {
  if (drafts.length === 0) return [];
  const { ref: canonical } = parseAuditRef(ref);
  const withRef = drafts.map((d) => ({ ...d, ParentRef: canonical }));
  const untagged = withRef.filter((d) => !d.auditId || !(d.Note ?? '').includes(actionTag(d.auditId)));
  const tagged = withRef.filter((d) => !untagged.includes(d));
  if (tagged.length === 0) return untagged;
  let existing: SiblingRow[];
  try {
    existing = await listAuditSiblings(io, canonical);
  } catch {
    return withRef;
  }
  const toPost = tagged.filter((d) => !alreadyPosted(existing, d));
  if (toPost.length === 0) return untagged;
  let auditList: string;
  try {
    auditList = await listIdOf(io, 'audit');
  } catch {
    return [...untagged, ...toPost];
  }
  return [...untagged, ...(await postInOrder(io, auditList, toPost))];
}
